import ComptoirShell from '@/components/comptoir/ComptoirShell'
import CustomerCreationModal from '@/components/comptoir/CustomerCreationModal'
import CustomerPicker from '@/components/comptoir/CustomerPicker'
import EmptyComptoirState from '@/components/comptoir/EmptyComptoirState'
import OrdersScopeToggle from '@/components/comptoir/OrdersScopeToggle'
import OrderCard from '@/components/orders/OrderCard'
import { useCustomers } from '@/queries/customers'
import { buildCatalogFor, createDraftOrder } from '@/utils/orders'
import { Contract, Customer, Order, OrdersScope, SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { Select, Stack, Text } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { useEffect, useState } from 'react'

function ComptoirBoutiquePage({
  orders,
  contracts,
}: {
  orders: Order[]
  contracts: Contract[]
}) {
  const { current_organization, current_user, user_role } = usePage<SharedProps>().props
  const organizationSlug = current_organization!.slug
  const autoAttachContractId = contracts.length === 1 ? contracts[0].id : null
  const needsContractPicker = contracts.length > 1

  const [opened, { open, close }] = useDisclosure(false)
  const [customerCreationOpened, customerCreation] = useDisclosure(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedCustomerSearch] = useDebouncedValue(customerSearch, 200)
  const [contractId, setContractId] = useState<number | null>(null)
  const [newlyCreatedOrderId, setNewlyCreatedOrderId] = useState<number | null>(null)
  const [ordersScope, setOrdersScope] = useState<OrdersScope>('mine')

  const { data: customers = [], isFetching: customersLoading } = useCustomers({
    organizationSlug,
    query: debouncedCustomerSearch,
  })

  const myOrders = orders.filter((order) => order.created_by_id === current_user?.id)
  const visibleOrders = ordersScope === 'mine' ? myOrders : orders

  const catalogFor = buildCatalogFor(contracts, [])

  const submitOrder = (customerId: number | null) =>
    createDraftOrder({
      organizationSlug,
      customerId,
      contractId: autoAttachContractId ?? contractId,
      onSuccess: (orderId) => {
        close()
        if (orderId) setNewlyCreatedOrderId(orderId)
      },
    })

  const handleCustomerCreated = (customer: Customer) => {
    customerCreation.close()
    if (needsContractPicker) {
      open()
      setCustomerSearch(customer.phone_number)
    } else {
      submitOrder(customer.id)
    }
  }

  useEffect(() => {
    if (!opened) {
      setContractId(null)
      setCustomerSearch('')
    }
  }, [opened])

  const customerPickerDisabled = needsContractPicker && !contractId

  const modalChildren = (
    <>
      {needsContractPicker && (
        <Select
          label="Contrat"
          placeholder="Choisir un contrat"
          data={contracts.map((contract) => ({
            value: String(contract.id),
            label: contract.provider_organization?.name ?? contract.customer_name,
          }))}
          value={contractId ? String(contractId) : null}
          onChange={(value) => setContractId(value ? Number(value) : null)}
          searchable
          required
        />
      )}
      <CustomerPicker
        customers={customers}
        searchValue={customerSearch}
        onSearchChange={setCustomerSearch}
        onCustomerSelect={(customer) => submitOrder(customer.id)}
        onNewCustomerClick={customerCreation.open}
        isLoading={customersLoading}
        disabled={customerPickerDisabled}
      />
    </>
  )

  return (
    <>
      <ComptoirShell
        title="Comptoir"
        opened={opened}
        onOpen={open}
        onClose={close}
        showNewOrderButton={user_role !== 'atelier'}
        modalChildren={modalChildren}
      >
        {orders.length === 0 && <EmptyComptoirState />}

        {orders.length > 0 && (
          <Stack>
            <OrdersScopeToggle
              value={ordersScope}
              onChange={setOrdersScope}
              mineCount={myOrders.length}
              allCount={orders.length}
            />
            {visibleOrders.length === 0 ? (
              <Text c="dimmed" size="sm">
                Vous n'avez aucune commande en cours. Affichez « Toutes » pour voir celles de l'équipe.
              </Text>
            ) : (
              visibleOrders.map((order: Order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  organizationSlug={organizationSlug}
                  articleServices={catalogFor(order)}
                  autoOpen={order.id === newlyCreatedOrderId}
                />
              ))
            )}
          </Stack>
        )}
      </ComptoirShell>

      <CustomerCreationModal
        opened={customerCreationOpened}
        onClose={customerCreation.close}
        onCreated={handleCustomerCreated}
        organizationSlug={organizationSlug}
      />
    </>
  )
}

export default ComptoirBoutiquePage
